import { SHARED_SENTRY_IGNORE_ERRORS } from "@babysits/islands/src/shared/sentry-ignore-errors";
import * as Sentry from "@sentry/browser";

// Development hosts never report. Decided from the hostname rather than a
// template-provided environment attribute so there is nothing in Twig that can
// be removed by accident and silently stop reporting.
const DEVELOPMENT_HOSTNAME = /\.development\.babysits\.net$/;

export const isDevelopmentHostname = (hostname: string): boolean =>
  DEVELOPMENT_HOSTNAME.test(hostname);

// Errors only for now: no tracing/replay integrations, so this doesn't touch
// the performance/replay quota. Extend integrations here once a page needs more.
export const sentryInit = (appName: string): void => {
  if (isDevelopmentHostname(window.location.hostname)) {
    return;
  }

  Sentry.init({
    dsn: "https://f3c03126f18823e44011bfa7ee0e47b7@o4507096105549824.ingest.de.sentry.io/4508494388133968",
    // Release version or build SHA, injected at build time — see rsbuild.config.ts.
    release: process.env.APP_VERSION ? `babysits-frontend@${process.env.APP_VERSION}` : undefined,
    environment: `prod-${appName}`,
    ignoreErrors: [
      ...SHARED_SENTRY_IGNORE_ERRORS,
      "Failed to initialize WebGL", // maplibre-gl: no WebGL context available (disabled/blocklisted GPU)
      "WebGL2 is required to display this map", // maplibre-gl v6 wording of the same; supportsWebGl2() should prevent it, this is the safety net
      // maplibre-gl lazy-loads the RTL text plugin from tile.ts when a tile has
      // right-to-left labels; that internal call has no error handling, so a
      // failed download of the plugin script surfaces as an unhandled rejection
      // our setRTLTextPlugin().catch() never sees (BABYSITS-FRONTEND-13K).
      // Non-fatal: the map still works, RTL labels just render unshaped.
      "RTL Text Plugin failed to import scripts",
      // A client whose getContext("webgl2") returns a stand-in object: non-null, so it passes
      // both maplibre-gl's null check and supportsWebGl2(), yet without the rendering methods,
      // so the painter's first GL call dies inside `new Map()` and again from the container
      // ResizeObserver. Every event is the same signature: Chrome 145 on Windows, no user, in
      // bursts of one profile page per second across the SG/HK/AE search and profile pages,
      // since 2026-09-21: a crawler with a stubbed WebGL context, not a user. TEMPORARY: drop
      // this entry once the probe rejects such contexts — see BABYSITS-FRONTEND-13P, -153, -154.
      "this.gl.viewport is not a function",
      "Maximum call stack size exceeded", // minified recursive frames, unresolvable without sourcemaps
      "Search request failed: 502", // upstream gateway error, not a frontend bug
      "Search request failed: 503", // upstream unavailable, not a frontend bug
      "Search request failed: 504", // upstream gateway timeout, not a frontend bug
      // Server-side failures on the markers endpoint (BABYSITS-FRONTEND-10V groups every
      // markers status under one issue; only the 5xx ones are dropped here, the 4xx ones
      // such as 422 are frontend-relevant and keep reporting).
      "Markers request failed: 500", // backend error, belongs to backend monitoring
      "Markers request failed: 502", // upstream gateway error, not a frontend bug
      "Markers request failed: 503", // upstream unavailable, not a frontend bug
      "Markers request failed: 504", // upstream gateway timeout, not a frontend bug
      "Places request failed: 502", // upstream gateway error, not a frontend bug
      "Places request failed: 504", // upstream gateway timeout, not a frontend bug
    ],
  });
};
